import { labelPembayaran } from '../utils/labelPembayaran';
import React, { useState, useEffect } from 'react';
import {
  Box,
  CircularProgress,
  Card,
  CardHeader,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableContainer,
  TableRow,
  TextField,
  Typography,
  Button,
  Grid,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Chip,
  TablePagination,
  Alert,
  Snackbar,
  Stack
} from '@mui/material';
import {
  List as ListIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
  Visibility as VisibilityIcon,
} from '@mui/icons-material';
import useSWR from 'swr';
import axios from 'axios';
import { useDispatch, useSelector } from "react-redux";
import { NavLink, useNavigate } from "react-router-dom";
import { apiHost } from '../utils/apiHost';
import { konfirmasi } from '../utils/dialog';

const customerStatusMap = {
  settlement: { label: 'Sukses', color: 'success' },
  pending: { label: 'Pending', color: 'warning' },
  dibuat: { label: 'Pending', color: 'warning' },
  deny: { label: 'Gagal', color: 'error' },
  cancel: { label: 'Gagal', color: 'error' },
  expire: { label: 'Gagal', color: 'error' },
  failure: { label: 'Gagal', color: 'error' },
};

const getApiBaseUrl = () => {
  const protocol = window.location.protocol === 'https:' ? 'https' : 'http';
  const baseUrl = apiHost().replace(/^https?:\/\//, '');
  return `${protocol}://${baseUrl}`;
};

// Create an Axios instance with the dynamic base URL and credentials
const axiosInstance = axios.create({
  baseURL: getApiBaseUrl(),
  withCredentials: true,
});

// Fetcher function for useSWR
const fetcher = (url) => axiosInstance.get(url).then((res) => res.data);

export const Customer = () => {
  const navigate = useNavigate();
  // State untuk pagination dan search
  const { user } = useSelector((state) => state.auth);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCabang, setSelectedCabang] = useState('');
  
  // State untuk dialog
  const [openDialog, setOpenDialog] = useState(false);
  const [dialogMode, setDialogMode] = useState('create'); // 'create' | 'edit'
  const [selectedCustomer, setSelectedCustomer] = useState(null);

  // State untuk dialog detail customer
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [detailCustomer, setDetailCustomer] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailPage, setDetailPage] = useState(0);
  const [detailRowsPerPage, setDetailRowsPerPage] = useState(10);
  
  // State untuk form
  const [formData, setFormData] = useState({
    nama: '',
    telepon: '',
    email: '',
    alamat: '',
    tanggalLahir: '',
    // --- PERBAIKAN: Ubah menjadi camelCase untuk sesuai backend ---
    kategoriCustomerUuid: '', 
    // --- Akhir PERBAIKAN ---
    cabangUuid: ''
  });
  
  // State untuk loading dan notifikasi
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });
  
  const [userInfo, setUserInfo] = useState(null);
  const { data: userProfile } = useSWR('/me', fetcher);
  const currentUser = user || userProfile?.data;
  
  // Fetch data cabang untuk superadmin
  const { data: cabangList } = useSWR(
    currentUser?.role === 'superadmin' ? '/cabang' : null,
    fetcher
  );
  
  // Fetch data kategori customer
  const { data: categoryList } = useSWR('/categorycustomer', fetcher);
  
  const buildCustomerUrl = () => {
    const params = new URLSearchParams();
    params.append('page', page + 1);
    params.append('limit', rowsPerPage);
    
    if (searchQuery) {
      params.append('search', searchQuery);
    }
    
    if (selectedCabang && currentUser?.role === 'superadmin') {
      params.append('cabanguuid', selectedCabang);
    }
    
    return `/getcustomer?${params.toString()}`;
  };
  
  const { data: customerData, error, mutate } = useSWR(
    currentUser ? buildCustomerUrl() : null,
    fetcher
  );
  
  useEffect(() => {
    if (currentUser) {
      setUserInfo(currentUser);
    }
  }, [currentUser]);
  
  // Reset form data
  const resetFormData = () => {
    setFormData({
      nama: '',
      telepon: '',
      email: '',
      alamat: '',
      tanggalLahir: '',
      // --- PERBAIKAN: Ubah menjadi camelCase ---
      kategoriCustomerUuid: '', 
      // --- Akhir PERBAIKAN ---
      cabangUuid: ''
    });
  };
  
  // Handle form input change
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  // Handle search
  const handleSearch = () => {
    setPage(0);
    mutate();
  };
  
  // Handle clear search
  const handleClearSearch = () => {
    setSearchQuery('');
    setSelectedCabang('');
    setPage(0);
    mutate();
  };
  
  // Handle create customer
  const handleCreateCustomer = () => {
    setDialogMode('create');
    resetFormData();
    setOpenDialog(true);
  };
  
  // Handle edit customer
  const handleEditCustomer = (customer) => {
    setDialogMode('edit');
    setSelectedCustomer(customer);
    setFormData({
      nama: customer.nama || '',
      telepon: customer.telepon || '',
      email: customer.email || '',
      alamat: customer.alamat || '',
      tanggalLahir: customer.tanggalLahir || '',
      // --- PERBAIKAN: Pastikan saat edit, ambil dari field yang benar di customer object ---
      kategoriCustomerUuid: customer.kategoriCustomerUuid || '', // customer.kategoricustomeruuid --> customer.kategoriCustomerUuid
      // --- Akhir PERBAIKAN ---
      cabangUuid: customer.cabanguuid || ''
    });
    setOpenDialog(true);
  };

  // Handle lihat detail customer (riwayat pesanan)
  const handleViewDetail = async (customer) => {
    setDetailDialogOpen(true);
    setDetailLoading(true);
    setDetailCustomer(null);
    setDetailPage(0);
    setDetailRowsPerPage(10);
    try {
      const response = await axiosInstance.get(`/customerbyid/${customer.uuid}`);
      setDetailCustomer(response.data?.data || null);
    } catch (err) {
      setSnackbar({
        open: true,
        message: err.response?.data?.message || 'Gagal mengambil detail customer',
        severity: 'error'
      });
      setDetailDialogOpen(false);
    } finally {
      setDetailLoading(false);
    }
  };

  // Handle submit form
  const handleSubmitForm = async () => {
    try {
      setLoading(true);
      
      let payload = {
        nama: formData.nama,
        telepon: formData.telepon,
        email: formData.email,
        alamat: formData.alamat,
        tanggalLahir: formData.tanggalLahir || null, // untuk promo ulang tahun
        // --- PERBAIKAN: Ubah menjadi camelCase ---
        kategoriCustomerUuid: formData.kategoriCustomerUuid 
        // --- Akhir PERBAIKAN ---
      };
      
      // Jika superadmin, tambahkan cabangUuid
      if (currentUser?.role === 'superadmin') {
        payload.cabangUuid = formData.cabangUuid;
      }
      
      if (dialogMode === 'create') {
        await axiosInstance.post('/createcustomer', payload);
        setSnackbar({
          open: true,
          message: 'Customer berhasil ditambahkan',
          severity: 'success'
        });
      } else {
        await axiosInstance.patch(`/updatecustomer/${selectedCustomer.uuid}`, payload);
        setSnackbar({
          open: true,
          message: 'Customer berhasil diperbarui',
          severity: 'success'
        });
      }
      
      setOpenDialog(false);
      resetFormData();
      mutate();
    } catch (error) {
      setSnackbar({
        open: true,
        message: error.response?.data?.message || 'Terjadi kesalahan',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };
  
  // Handle delete customer
  const handleDeleteCustomer = async (customer) => {
    if (await konfirmasi(`Apakah Anda yakin ingin menghapus customer ${customer.nama}?`)) {
      try {
        setLoading(true);
        await axiosInstance.delete(`/deletecustomer/${customer.uuid}`);
        setSnackbar({
          open: true,
          message: 'Customer berhasil dihapus',
          severity: 'success'
        });
        mutate();
      } catch (error) {
        setSnackbar({
          open: true,
          message: error.response?.data?.message || 'Terjadi kesalahan',
          severity: 'error'
        });
      } finally {
        setLoading(false);
      }
    }
  };
  
  // Handle pagination
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };
  
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };
  
  // Function to get category name by UUID
  const getCategoryNameByUuid = (categoryUuid) => {
    const category = categoryList?.data?.find(cat => cat.uuid === categoryUuid);
    return category?.namaKategoriCustomer || categoryUuid;
  };
  
  // Loading state
  if (!userProfile && !error) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }
  
  return (
    <Box sx={{ p: 3 }}>
      <Card>
        <CardHeader
          title="Data Customer"
          action={
            <Stack direction="column" spacing={1}>
              <Button
                variant="contained"
                color="primary"
                startIcon={<AddIcon />}
                onClick={handleCreateCustomer}
              >
                Tambah Customer
              </Button>
              <Button
                variant="contained"
                color="primary"
                startIcon={<ListIcon />}
                component={NavLink}
                to={"/transaksicustomer"}
              >
                Transaksi Customer
              </Button>
            </Stack>
          }
        />
        
        {/* Search and Filter Section */}
        <Box sx={{ p: 2 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Cari Customer"
                placeholder="Nama, email, atau telepon"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                InputProps={{
                  endAdornment: (
                    <IconButton onClick={handleSearch}>
                      <SearchIcon />
                    </IconButton>
                  )
                }}
              />
            </Grid>
            
            {/* Filter Cabang hanya untuk superadmin */}
            {currentUser?.role === 'superadmin' && (
              <Grid item xs={12} md={4}>
                <FormControl fullWidth>
                  <InputLabel>Filter Cabang</InputLabel>
                  <Select
                    value={selectedCabang}
                    onChange={(e) => setSelectedCabang(e.target.value)}
                    label="Filter Cabang"
                  >
                    <MenuItem value="">
                      <em>Semua Cabang</em>
                    </MenuItem>
                    {cabangList?.data?.map((cabang) => (
                      <MenuItem key={cabang.uuid} value={cabang.uuid}>
                        {cabang.namacabang}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            )}
            
            <Grid item xs={12} md={4}>
              <Button
                variant="outlined"
                startIcon={<ClearIcon />}
                onClick={handleClearSearch}
              >
                Clear Filter
              </Button>
            </Grid>
          </Grid>
        </Box>
        
        {/* Table Section */}
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Nama</TableCell>
                <TableCell>Telepon</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Alamat</TableCell>
                <TableCell>Tgl lahir</TableCell>
                <TableCell>Kategori</TableCell>
                <TableCell>Cabang</TableCell>
                <TableCell>Total Pesanan</TableCell>
                <TableCell>Aksi</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {customerData?.data?.map((customer) => (
                <TableRow key={customer.uuid}>
                  <TableCell>{customer.nama}</TableCell>
                  <TableCell>{customer.telepon}</TableCell>
                  <TableCell>{customer.email || '-'}</TableCell>
                  <TableCell>{customer.alamat || '-'}</TableCell>
                  <TableCell>{customer.tanggalLahir ? new Date(`${customer.tanggalLahir}T00:00:00`).toLocaleDateString('id-ID', { day: 'numeric', month: 'short', year: 'numeric' }) : '-'}</TableCell>
                  <TableCell>
                    {/* --- PERBAIKAN: Pastikan tampilkan category.namaKategoriCustomer yang benar --- */}
                    {customer.KategoriCustomer?.namaKategoriCustomer && ( // Gunakan KategoriCustomer object jika di-include
                        <Chip 
                            label={customer.KategoriCustomer.namaKategoriCustomer} 
                            size="small" 
                        />
                    )}
                    {/* Jika tidak di-include, fallback ke fungsi lama */}
                    {!customer.KategoriCustomer && customer.kategoriCustomerUuid && (
                        <Chip 
                            label={getCategoryNameByUuid(customer.kategoriCustomerUuid)} 
                            size="small" 
                        />
                    )}
                    {/* --- Akhir PERBAIKAN --- */}
                  </TableCell>
                  <TableCell>{customer.Cabang?.namacabang}</TableCell>
                  <TableCell>{customer.totalPesanan ?? 0}</TableCell>
                  <TableCell>
                    <IconButton
                      color="default"
                      onClick={() => handleViewDetail(customer)}
                    >
                      <VisibilityIcon />
                    </IconButton>
                    <IconButton
                      color="primary"
                      onClick={() => handleEditCustomer(customer)}
                    >
                      <EditIcon />
                    </IconButton>
                    {currentUser?.role === 'superadmin' && (
                      <IconButton 
                        color="error" 
                        onClick={() => handleDeleteCustomer(customer)}
                      >
                        <DeleteIcon />
                      </IconButton>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        
        {/* Pagination */}
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={customerData?.pagination?.totalItems || 0}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Card>
      
      {/* Dialog for Create/Edit Customer */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          {dialogMode === 'create' ? 'Tambah Customer' : 'Edit Customer'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Nama"
                name="nama"
                value={formData.nama}
                onChange={handleInputChange}
                required
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Telepon"
                name="telepon"
                value={formData.telepon}
                onChange={handleInputChange}
                required
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleInputChange}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Kategori</InputLabel>
                <Select
                  // --- PERBAIKAN: Ubah name menjadi camelCase ---
                  name="kategoriCustomerUuid" 
                  // --- Akhir PERBAIKAN ---
                  value={formData.kategoriCustomerUuid} // Menggunakan camelCase
                  onChange={handleInputChange}
                  label="Kategori"
                >
                  <MenuItem value="">
                    <em>Pilih Kategori</em>
                  </MenuItem>
                  {categoryList?.data?.map((category) => (
                    <MenuItem key={category.uuid} value={category.uuid}>
                      {category.namaKategoriCustomer}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                fullWidth
                type="date"
                label="Tanggal lahir (opsional)"
                name="tanggalLahir"
                InputLabelProps={{ shrink: true }}
                inputProps={{ max: new Date().toISOString().slice(0, 10) }}
                helperText="Dipakai untuk promo ulang tahun"
                value={formData.tanggalLahir}
                onChange={handleInputChange}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Alamat"
                name="alamat"
                multiline
                rows={3}
                value={formData.alamat}
                onChange={handleInputChange}
              />
            </Grid>
            
            {/* Dropdown cabang hanya untuk superadmin */}
            {currentUser?.role === 'superadmin' && (
              <Grid item xs={12}>
                <FormControl fullWidth required>
                  <InputLabel>Cabang</InputLabel>
                  <Select
                    name="cabangUuid"
                    value={formData.cabangUuid}
                    onChange={handleInputChange}
                    label="Cabang"
                  >
                    <MenuItem value="">
                      <em>Pilih Cabang</em>
                    </MenuItem>
                    {cabangList?.data?.map((cabang) => (
                      <MenuItem key={cabang.uuid} value={cabang.uuid}>
                        {cabang.namacabang}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>
            Batal
          </Button>
          <Button
            onClick={handleSubmitForm}
            variant="contained"
            disabled={loading || !formData.nama || !formData.telepon || 
              (currentUser?.role === 'superadmin' && !formData.cabangUuid)}
          >
            {loading ? 'Loading...' : (dialogMode === 'create' ? 'Tambah' : 'Update')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog Detail Customer - riwayat pesanan */}
      <Dialog
        open={detailDialogOpen}
        onClose={() => setDetailDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Detail Customer{detailCustomer ? ` - ${detailCustomer.nama}` : ''}
        </DialogTitle>
        <DialogContent>
          {detailLoading ? (
            <Box display="flex" justifyContent="center" p={4}>
              <CircularProgress />
            </Box>
          ) : detailCustomer ? (
            <>
              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={6} sm={3}>
                  <Paper sx={{ p: 2, textAlign: 'center' }} elevation={0} variant="outlined">
                    <Typography variant="h5" fontWeight="bold">{detailCustomer.summary?.totalSukses ?? 0}</Typography>
                    <Typography variant="body2" color="success.main">Sukses</Typography>
                  </Paper>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Paper sx={{ p: 2, textAlign: 'center' }} elevation={0} variant="outlined">
                    <Typography variant="h5" fontWeight="bold">{detailCustomer.summary?.totalPending ?? 0}</Typography>
                    <Typography variant="body2" color="warning.main">Pending</Typography>
                  </Paper>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Paper sx={{ p: 2, textAlign: 'center' }} elevation={0} variant="outlined">
                    <Typography variant="h5" fontWeight="bold">{detailCustomer.summary?.totalGagal ?? 0}</Typography>
                    <Typography variant="body2" color="error.main">Gagal</Typography>
                  </Paper>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Paper sx={{ p: 2, textAlign: 'center' }} elevation={0} variant="outlined">
                    <Typography variant="h5" fontWeight="bold">{detailCustomer.summary?.totalSemua ?? 0}</Typography>
                    <Typography variant="body2" color="text.secondary">Total Semua</Typography>
                  </Paper>
                </Grid>
              </Grid>

              <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
                Riwayat Pesanan
              </Typography>
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Order ID</TableCell>
                      <TableCell>Tanggal</TableCell>
                      <TableCell>Metode</TableCell>
                      <TableCell>Total</TableCell>
                      <TableCell>Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(detailCustomer.transaksi || []).length === 0 && (
                      <TableRow>
                        <TableCell colSpan={5} align="center">Belum ada pesanan</TableCell>
                      </TableRow>
                    )}
                    {(detailCustomer.transaksi || [])
                      .slice(detailPage * detailRowsPerPage, detailPage * detailRowsPerPage + detailRowsPerPage)
                      .map((trx) => (
                      <TableRow key={trx.uuid}>
                        <TableCell>{trx.order_id}</TableCell>
                        <TableCell>{new Date(trx.tanggal || trx.createdAt).toLocaleDateString('id-ID')}</TableCell>
                        <TableCell sx={{ textTransform: 'uppercase' }}>{labelPembayaran(trx)}</TableCell>
                        <TableCell>
                          {new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR' }).format(trx.totaljual || 0)}
                        </TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            label={customerStatusMap[trx.status_pembayaran]?.label || trx.status_pembayaran}
                            color={customerStatusMap[trx.status_pembayaran]?.color || 'default'}
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                {(detailCustomer.transaksi || []).length > 0 && (
                  <TablePagination
                    component="div"
                    count={(detailCustomer.transaksi || []).length}
                    page={detailPage}
                    onPageChange={(e, newPage) => setDetailPage(newPage)}
                    rowsPerPage={detailRowsPerPage}
                    onRowsPerPageChange={(e) => {
                      setDetailRowsPerPage(parseInt(e.target.value, 10));
                      setDetailPage(0);
                    }}
                    rowsPerPageOptions={[10, 25, 50]}
                  />
                )}
              </TableContainer>
            </>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailDialogOpen(false)}>Tutup</Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};